import React, { useState, useEffect, useRef, createContext, useContext, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Achievements from './Achievements';
import Products from './Products';
import Contact from './Contact';
import LineGrid from './LineGrid';
import { gsap } from 'gsap';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import { Volume2, VolumeX, Eye, Rocket, Mail, Phone, MapPin, CheckCircle, Loader2, AlertCircle, ChevronDown, Home as HomeIcon, Package as PackageIcon, Trophy, Info } from 'lucide-react';
import emailjs from '@emailjs/browser';
import './App.css'

import chaviLogo from './assets/chavilogo.png';
import Performance from './assets/Performance.png';
import Perf2 from './assets/Perf2.png';
import FIEO from './assets/FIEO.jpg';
import Kt from './assets/Kt.png';
import CGS from './assets/CGS.png';
import cgstheme from './assets/cgstheme.mp3';
import HBM from './assets/HBM.png';
import TOM from './assets/TOM.png';
import CPM from './assets/CPM.png';
import RDM from './assets/RDM.png';
import BBM from './assets/BBM.png';
import HydroBM from './assets/HydroBM.png';
import HDPE from './assets/HDPE.png';
import Bento from './assets/Bento.png';
import TW from './assets/TW.jpg';

gsap.registerPlugin(InertiaPlugin);

const ThemeContext = createContext();

export const ScrollContext = createContext({ scrollContainer: null, setScrollContainer: () => {} });

const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState('dark');

    useEffect(() => {
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme }}>
            {children}
        </ThemeContext.Provider>
    );
};

const NavBar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [hidden, setHidden] = useState(false);
    const { scrollContainer } = useContext(ScrollContext);
    const location = useLocation();

    useEffect(() => {
        const element = scrollContainer || window;
        let lastScrollY = scrollContainer ? scrollContainer.scrollTop : window.scrollY;

        const handleScroll = () => {
            const currentScrollY = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
            if (currentScrollY > lastScrollY && currentScrollY > 10) {
                setHidden(true);
            } else {
                setHidden(false);
            }
            lastScrollY = currentScrollY <= 0 ? 0 : currentScrollY;
        };

        element.addEventListener('scroll', handleScroll);
        return () => {
            element.removeEventListener('scroll', handleScroll);
        };
    }, [scrollContainer]);

    const navLinks = [
        { name: 'Home', path: '/', Icon: HomeIcon },
        { name: 'Products', path: '/products', Icon: PackageIcon },
        { name: 'Contact', path: '/contact', Icon: Mail }
    ];
    
    const socialLinks = [
        { name: 'X', href: "https://x.com/chavigroup?t=cwjWJndBAzXOZW2NiAy4Yw&s=09", icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg> },
        { name: 'LinkedIn', href: "https://www.linkedin.com/in/r-sanjay-bb6179173?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app", icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.783-1.75-1.75s.784-1.75 1.75-1.75 1.75.783 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg> },
        { name: 'Instagram', href: "https://www.instagram.com/chavigroup?igsh=czIzNGE0MzVtaXZs", icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="32"><rect x="48" y="48" width="416" height="416" rx="96" ry="96" fill="none" stroke="currentColor" strokeWidth="32" strokeLinejoin="round"/><circle cx="256" cy="256" r="88" fill="none" stroke="currentColor" strokeWidth="32" strokeLinecap="round" strokeLinejoin="round"/><circle cx="360" cy="152" r="20" fill="currentColor" stroke="currentColor" strokeWidth="28" strokeLinecap="round" strokeLinejoin="round"/></svg> }
    ];

    return (
        <motion.nav
            initial={{ y: 0, opacity: 1 }}
            animate={{ y: hidden ? -120 : 0, opacity: hidden ? 0 : 1 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-6"
        >
            <div className="w-full max-w-2xl mx-4">
                <div className="relative flex items-center justify-between bg-black/10 backdrop-blur-[24px] rounded-full border border-white/10 shadow-lg px-6 py-2">
                    <Link to="/" className="flex items-center gap-2 shrink-0">
                        <img src={chaviLogo} alt="Chavi Global Logo" className="w-20 h-10 sm:w-24 sm:h-12 object-contain" />
                        <span className="text-xl sm:text-2xl font-bold text-white block font-heading">Chavi Group</span>
                    </Link>
                    <div className="hidden lg:flex items-center gap-10">
                        {navLinks.map(({ name, path, Icon }) => (
                            <Link
                                key={name}
                                to={path}
                                className={`group relative transition-colors duration-300 ${location.pathname === path ? 'text-white' : 'text-neutral-400 hover:text-white'}`}
                            >
                                <Icon className="w-6 h-6" />
                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-black text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-bold">
                                    {name}
                                </span>
                            </Link>
                        ))}
                    </div>
                    <div className="lg:hidden">
                        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white p-2">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isMenuOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
                            </svg>
                        </button>
                    </div>
                </div>
                <AnimatePresence>
                    {isMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: -20, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: -20, height: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="lg:hidden bg-black/20 backdrop-blur-[24px] rounded-2xl mt-2 overflow-hidden border border-white/10"
                        >
                            <div className="flex flex-col items-center gap-2 p-4">
                                {navLinks.map(({ name, path }) => (
                                    <Link key={name} to={path} className="w-full text-center py-3 px-4 rounded-lg text-lg font-medium text-white transition-colors duration-300 hover:bg-white/10 font-heading" onClick={() => setIsMenuOpen(false)}>
                                        {name}
                                    </Link>
                                ))}
                                 <div className="w-full h-px bg-white/10 my-2"></div>
                                <div className="flex items-center justify-center gap-6 mt-2">
                                    {socialLinks.map(({ name, href, icon }) => (
                                        <a key={name} href={href} target="_blank" rel="noopener noreferrer" className="text-white p-2 rounded-full hover:bg-white/10 transition-colors">
                                            {icon}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.nav>
    );
};

const Footer = () => (
    <footer className="w-full py-4">
        <p className="text-center text-sm text-neutral-600 dark:text-neutral-500">
            © 2025 Chavi Group . All rights reserved.
        </p>
    </footer>
);

const PageWrapper = ({ children }) => {
    const location = useLocation();

    return (
        <AnimatePresence mode="wait" onExitComplete={() => window.scrollTo(0, 0)}>
            <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="h-full"
            >
                {children}
            </motion.div>
        </AnimatePresence>
    );
};

const ImagePopup = ({ image, title, onClose }) => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} className="relative max-w-4xl w-full bg-neutral-100 dark:bg-neutral-900/80 backdrop-blur-lg border border-black/10 dark:border-white/10 rounded-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <button onClick={onClose} className="absolute -top-4 -right-4 bg-white dark:bg-black text-black dark:text-white rounded-full p-2 hover:bg-gray-200 dark:hover:bg-neutral-800 transition-colors z-10"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
            <div className="relative"><img src={image} alt={title} className="w-full h-[70vh] object-contain rounded-t-lg" /></div>
            <div className="p-5 bg-neutral-100 dark:bg-neutral-900/80 rounded-b-lg"><h3 className="text-xl font-semibold text-black dark:text-white font-heading">{title}</h3></div>
        </motion.div>
    </motion.div>
);

const CardContent = ({ data, className = "", size = 'default' }) => {
    const Icon = data.icon;
    const cardRef = useRef(null);

    const handleMouseMove = (e) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        cardRef.current.style.setProperty('--mouse-x', `${x}px`);
        cardRef.current.style.setProperty('--mouse-y', `${y}px`);
    };

    return (
        <div className="flex flex-col items-center z-10">
            <div className="w-[3px] h-8 bg-gradient-to-b from-black/10 via-black/40 to-black/10 dark:from-white/10 dark:via-white/40 dark:to-white/10 rounded-full" />
            <motion.div
                ref={cardRef}
                onMouseMove={handleMouseMove}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.5 }}
                variants={{
                    hidden: { rotate: 0 },
                    visible: {
                        rotate: [-1, 1, -1],
                        transition: {
                            duration: 4,
                            ease: "easeInOut",
                            repeat: Infinity,
                            repeatType: "mirror"
                        }
                    }
                }}
                className={`group relative bg-white/60 dark:bg-black/60 backdrop-blur-xl border border-black/10 dark:border-white/10 rounded-3xl shadow-2xl overflow-hidden p-px ${className}`}
            >
                <div className="absolute inset-0 bg-white/80 dark:bg-black/80 rounded-3xl transition-all duration-500 group-hover:opacity-0" />
                <div className={`relative z-10 flex flex-col items-center text-center h-full bg-transparent rounded-3xl ${size === 'small' ? 'p-4' : 'p-6'}`}>
                    <div className={`bg-gray-200 dark:bg-neutral-800 rounded-xl border border-black/10 dark:border-neutral-700 mb-4 ${size === 'small' ? 'p-3' : 'p-3'}`}><Icon className={`text-black dark:text-white ${size === 'small' ? 'w-5 h-5' : 'w-6 h-6'}`} /></div>
                    <h2 className={`font-bold text-black dark:text-white mb-2 font-heading ${size === 'small' ? 'text-lg' : 'text-2xl sm:text-3xl'}`}>{data.title}</h2>
                    <p className={`text-neutral-600 dark:text-neutral-300 leading-relaxed ${size === 'small' ? 'text-sm' : ''}`}>{data.text}</p>
                </div>
            </motion.div>
        </div>
    );
};

const VisionMissionSlider = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isInView, setIsInView] = useState(false);

    const aboutData = { 
        icon: Info, 
        title: "About Chavi", 
        text: "Chavi, a FIEO-registered exporter with 40+ years of experience, specializes in tape wrapping machines and industrial products, offering trusted sourcing and liaisoning services globally." 
    };
    const visionData = { icon: Eye, title: "Our Vision", text: "Pioneering a future where innovation and excellence create products that consistently exceed expectations." };
    const missionData = { icon: Rocket, title: "Our Mission", text: "To deliver world-class products in partnership with premier companies, ensuring global satisfaction and trust." };

    const slideData = useMemo(() => [
        { component: <CardContent data={aboutData} className="h-full w-full" /> },
        { component: <CardContent data={visionData} className="w-full" size="small" /> },
        { component: <CardContent data={missionData} className="w-full" size="small" /> }
    ], []);

    useEffect(() => {
        let intervalId;
        if (isInView) {
            setCurrentIndex(0);
            intervalId = setInterval(() => {
                setCurrentIndex(prevIndex => (prevIndex + 1) % slideData.length);
            }, 4000);
        }
        return () => clearInterval(intervalId);
    }, [isInView, slideData.length]);

    return (    
        <motion.div 
            initial={{ opacity: 0, y: 40 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            onViewportEnter={() => setIsInView(true)}
            onViewportLeave={() => setIsInView(false)}
            viewport={{ amount: 0.5 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} 
            className="w-full max-w-6xl mx-auto flex flex-col items-center h-full px-4"
        >
            <div className="text-center flex-shrink-0 pt-32">
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black dark:text-white mb-4 font-heading">About Us</h2>
                <div className="w-24 h-1 bg-black dark:bg-white rounded-full mx-auto"></div>
            </div>

            <div className="hidden lg:flex flex-1 w-full items-center justify-center">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 w-full items-stretch">
                    <div className="lg:col-span-3 h-full">
                        <CardContent data={aboutData} className="h-full w-full" />
                    </div>
                    <div className="lg:col-span-2 flex flex-col gap-4 h-full justify-around">
                        <CardContent data={visionData} className="w-full" size="small" />
                        <CardContent data={missionData} className="w-full" size="small" />
                    </div>
                </div>
            </div>

            <div className="lg:hidden w-full max-w-md flex flex-col flex-1 items-center justify-center py-4">
                <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                    <AnimatePresence initial={false}>
                        <motion.div
                            key={currentIndex}
                            initial={{ x: 300, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: -300, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                            className="absolute inset-0 flex items-center justify-center p-4"
                        >
                            {slideData[currentIndex].component}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
};

const FooterMusic = () => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(true);
    useEffect(() => {
        const playAudio = async () => {
            try {
                if (audioRef.current) {
                    audioRef.current.volume = 0.5;
                    await audioRef.current.play();
                }
            } catch (error) { setIsPlaying(false); }
        };
        playAudio();
    }, []);
    const togglePlay = () => {
        if (audioRef.current) {
            if (isPlaying) { audioRef.current.pause(); } else { audioRef.current.play().catch(() => setIsPlaying(false)); }
            setIsPlaying(!isPlaying);
        }
    };
    return (
        <div className="fixed bottom-6 right-6 z-50">
            <audio ref={audioRef} src={cgstheme} loop />
            <button onClick={togglePlay} className="bg-white/10 dark:bg-white/10 backdrop-blur-md text-black dark:text-white rounded-full p-3 shadow-lg hover:bg-white/20 dark:hover:bg-white/20 transition-all flex items-center justify-center">
                {isPlaying ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
            </button>
        </div>
    );
};

const SocialSidebar = () => (
    <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 1.5 }} className="fixed left-0 top-1/2 -translate-y-1/2 z-40 hidden lg:flex">
        <div className="flex flex-col items-center gap-2 bg-white/50 dark:bg-black/50 backdrop-blur-lg p-2 rounded-r-2xl border-y border-r border-black/10 dark:border-white/10">
            <a href="https://x.com/chavigroup?t=cwjWJndBAzXOZW2NiAy4Yw&s=09" target="_blank" rel="noopener noreferrer" className="text-black dark:text-white p-2 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
            </a>
            <a href="https://www.linkedin.com/in/r-sanjay-bb6179173?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app" target="_blank" rel="noopener noreferrer" className="text-black dark:text-white p-2 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.783-1.75-1.75s.784-1.75 1.75-1.75 1.75.783 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
            </a>
            <a href="https://www.instagram.com/chavigroup?igsh=czIzNGE0MzVtaXZs" target="_blank" rel="noopener noreferrer" className="text-black dark:text-white p-2 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="32">
                    <rect x="48" y="48" width="416" height="416" rx="96" ry="96"
                        fill="none" stroke="currentColor" strokeWidth="32" strokeLinejoin="round"/>
                    <circle cx="256" cy="256" r="88"
                        fill="none" stroke="currentColor" strokeWidth="32" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="360" cy="152" r="20"
                        fill="currentColor" stroke="currentColor" strokeWidth="28" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </a>
        </div>
    </motion.div>
);

const Home = () => {
    const contactRef = useRef(null);
    const location = useLocation();
    const achievementsRef = useRef(null);
    const { setScrollContainer } = useContext(ScrollContext);
    const homeScrollRef = useRef(null);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 1024);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setScrollContainer(homeScrollRef.current);
        if (location.hash === '#contact-section') {
            setTimeout(() => {
                contactRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
        if (location.hash === '#achievements-section') {
            achievementsRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
        return () => setScrollContainer(null);
    }, [location.hash, setScrollContainer]);

    return (
        <div ref={homeScrollRef} className={`h-full overflow-y-auto overflow-x-hidden scroll-smooth custom-scrollbar ${isDesktop ? 'snap-y snap-mandatory' : ''}`}>
            <section className={`h-screen flex items-center justify-center relative text-center px-4 overflow-hidden ${isDesktop ? 'snap-start' : ''}`}>
                <div className="absolute inset-0 -z-10">
                    <motion.div className="absolute -top-1/4 left-0 right-0 w-[200%] h-1/2 mx-auto bg-[#FF9933]/10 rounded-full blur-[150px] opacity-60" animate={{ y: [0, 20, 0] }} transition={{ duration: 25, repeat: Infinity, repeatType: 'mirror' }}></motion.div>
                    <motion.div className="absolute -bottom-1/4 left-0 right-0 w-[200%] h-1/2 mx-auto bg-[#138808]/10 rounded-full blur-[150px] opacity-60" animate={{ y: [0, -20, 0] }} transition={{ duration: 25, repeat: Infinity, repeatType: 'mirror', delay: 5 }}></motion.div>
                </div>
                <div className="relative z-10">
                    <motion.h1 className="text-6xl sm:text-7xl lg:text-9xl font-extrabold mb-8 leading-tight font-heading" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <span className="block bg-gradient-to-r from-orange-500 via-gray-600 to-green-700 dark:from-[#FF9933] dark:to-white bg-clip-text text-transparent">Namaste</span>
                        <span className="block text-black dark:text-white mt-2">from</span>
                        <span className="block bg-gradient-to-r from-gray-600 to-green-700 dark:from-white dark:to-[#138808] bg-clip-text text-transparent mt-2">India</span>
                    </motion.h1>
                    <motion.p className="text-lg sm:text-xl text-neutral-600 dark:text-neutral-300 max-w-3xl mx-auto" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
                        Delivering Indian excellence globally.
                    </motion.p>
                </div>
                <motion.div className="absolute bottom-20 animate-bounce text-black dark:text-white cursor-pointer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} onClick={() => document.getElementById('about-us-section')?.scrollIntoView({ behavior: 'smooth' })}>
                    <ChevronDown className="h-10 w-10" />
                </motion.div>
            </section>

            <section id="about-us-section" className={`h-screen relative overflow-hidden ${isDesktop ? 'snap-start' : ''}`}>
                <VisionMissionSlider />
            </section>

            <section id="achievements-section" ref={achievementsRef} className={`h-screen flex flex-col items-center justify-center p-4 relative ${isDesktop ? 'snap-start' : ''}`}>
                <Achievements/>
            </section>
            <section className={`${isDesktop ? 'snap-start' : ''} bg-black`}>
                <Footer />
            </section>
        </div>
    );
};


const GlobalStyles = () => (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@700;800&family=Inter:wght@400;500&display=swap');
      .font-heading { font-family: 'Exo 2', sans-serif; }
      html, body, #root {
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        overflow: hidden;
      }
      body { 
        font-family: 'Inter', sans-serif;
      }
      .custom-scrollbar::-webkit-scrollbar {
        width: 12px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background-color: #888;
        border-radius: 6px;
        border: 3px solid transparent;
        background-clip: content-box;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background-color: #555;
      }
    `}</style>
);


const App = () => {
    const [scrollContainer, setScrollContainer] = useState(null);
    const value = useMemo(() => ({ scrollContainer, setScrollContainer }), [scrollContainer]);

    return (
        <ThemeProvider>
            <ScrollContext.Provider value={value}>
                <GlobalStyles />
                <BrowserRouter>
                    <div className="relative h-full w-full flex flex-col">
                        <div className="absolute inset-0 bg-neutral-100 dark:bg-black -z-20" />
                        <LineGrid
                            baseColor="rgba(128,128,128,0.2)"
                            activeColor="rgba(255,255,255,0.5)"
                            proximity={75}
                            magnifyRadius={120}
                            magnifyStrength={1.2}
                            rippleAmplitude={4}
                            rippleSpeed={3} />
                        <NavBar />
                        <SocialSidebar />
                        <main className="flex-grow h-full overflow-hidden">
                            <Routes>
                                <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
                                <Route path="/products" element={<PageWrapper><Products /></PageWrapper>} />
                                <Route path="/contact" element={<PageWrapper><Contact /></PageWrapper>} />
                            </Routes>
                        </main>
                        <FooterMusic />
                    </div>
                </BrowserRouter>
            </ScrollContext.Provider>
        </ThemeProvider>
    );
};

export default App;
